import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Calendar, Mail, MapPin, ChevronDown, Facebook, Instagram, Youtube, Search, Wrench, Zap, Car, FileText } from 'lucide-react';
import { Button } from './ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { OptimizedImage } from './OptimizedImage';
import cargurooLogo from '@/assets/carguroo-logo-main.svg';
const services = [{
  name: 'Purchase Inspection',
  href: '/services/pre-purchase-inspection',
  icon: Search
}, {
  name: 'Car Servicing',
  href: '/services/car-servicing',
  icon: Wrench
}, {
  name: 'Diagnostics',
  href: '/services/diagnostics',
  icon: Zap
}, {
  name: 'Mobile Repairs',
  href: '/services/mobile-repairs',
  icon: Car
}];
export const ModernNav = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [activeSection, setActiveSection] = useState('home');
  const [sectionPositions, setSectionPositions] = useState<{
    [key: string]: {
      top: number;
      height: number;
    };
  }>({});
  const location = useLocation();

  // Cache section positions to avoid forced reflows
  useEffect(() => {
    const updateSectionPositions = () => {
      if (location.pathname === '/') {
        const sections = ['home', 'services', 'about', 'areas', 'contact'];
        const positions: {
          [key: string]: {
            top: number;
            height: number;
          };
        } = {};
        sections.forEach(section => {
          const element = document.getElementById(section);
          if (element) {
            positions[section] = {
              top: element.offsetTop,
              height: element.offsetHeight
            };
          }
        });
        setSectionPositions(positions);
      }
    };

    // Update positions on load and resize
    updateSectionPositions();
    window.addEventListener('resize', updateSectionPositions);
    return () => window.removeEventListener('resize', updateSectionPositions);
  }, [location.pathname]);
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const scrollPosition = window.scrollY;
          setIsVisible(scrollPosition < 100);
          if (location.pathname === '/' && Object.keys(sectionPositions).length > 0) {
            const scrollPos = scrollPosition + 100;
            const sections = ['home', 'services', 'about', 'areas', 'contact'];
            for (const section of sections) {
              const position = sectionPositions[section];
              if (position && scrollPos >= position.top && scrollPos < position.top + position.height) {
                setActiveSection(section);
                break;
              }
            }
          }
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname, sectionPositions]);
  const scrollToSection = (sectionId: string) => {
    if (location.pathname !== '/') {
      window.location.href = `/#${sectionId}`;
      return;
    }

    // Use requestAnimationFrame to prevent forced reflows
    requestAnimationFrame(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth'
        });
      }
    });
    setIsMobileMenuOpen(false);
  };
  const isHomePage = location.pathname === '/';
  return <>
      {/* Top Contact Bar - Shows on all devices */}
      <div className={`block lg:fixed relative top-0 left-0 right-0 z-50 bg-primary text-white py-1 transition-all duration-500 ${isVisible ? 'lg:translate-y-0 translate-y-0 opacity-100' : 'lg:-translate-y-full translate-y-0 lg:opacity-0 opacity-100 lg:pointer-events-none pointer-events-auto'}`}>
        <div className="max-w-7xl mx-auto px-4 flex justify-center items-center text-xs gap-2 lg:gap-8">
          <div className="flex items-center space-x-2 lg:space-x-6">
            <div className="flex items-center space-x-1">
              <Phone className="w-3 h-3 lg:w-4 lg:h-4" />
              <span>+61 440 134 765</span>
            </div>
            <div className="flex items-center space-x-1">
              <Mail className="w-3 h-3 lg:w-4 lg:h-4" />
              <span className="hidden sm:inline">info@carguroo.com.au</span>
            </div>
            
            <div className="hidden lg:flex items-center space-x-4 ml-8">
              <Facebook className="w-4 h-4 hover:text-primary-foreground cursor-pointer transition-colors" onClick={() => window.open('https://www.facebook.com/carguroomobilemechanic/', '_blank')} />
              <Instagram className="w-4 h-4 hover:text-primary-foreground cursor-pointer transition-colors" onClick={() => window.open('https://www.instagram.com/carguroo/', '_blank')} />
              
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation - Fixed on desktop, static on mobile */}
      <nav className={`lg:fixed relative left-0 right-0 z-40 transition-all duration-500 ${isVisible ? 'lg:top-[24px] top-0 translate-y-0 opacity-100' : 'lg:top-0 top-0 lg:-translate-y-full translate-y-0 lg:opacity-0 opacity-100 lg:pointer-events-none pointer-events-auto'} bg-white/95 backdrop-blur-lg shadow-lg border-b border-gray-200`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-12 lg:h-14 mobile-touch-spacing">
            
            {/* Premium Logo Section */}
            <Link to="/" className="flex items-center space-x-4 group relative z-10" aria-label="Carguroo - Return to homepage">
              <div className="relative flex items-center space-x-3">
                {/* Enhanced logo background with gradient glow */}
                <div className="absolute -inset-2 -left-4 bg-gradient-to-r from-primary/20 via-primary/10 to-primary/20 rounded-3xl blur-2xl group-hover:blur-3xl transition-all duration-700 opacity-0 group-hover:opacity-100 scale-95 group-hover:scale-110"></div>
                
                {/* Professional logo container */}
                <div className="relative flex items-center bg-white/98 backdrop-blur-md px-1.5 py-0.5 rounded-lg shadow-md group-hover:shadow-lg transition-all duration-500 border border-gray-200/60 hover:border-primary/40 hover:bg-white/100 hover:scale-102">
                  <div className="flex items-center justify-center">
                    <OptimizedImage src={cargurooLogo} alt="Carguroo - Professional Mobile Mechanic Services" className="h-6 lg:h-8 w-auto object-contain transition-all duration-700 group-hover:scale-110 filter group-hover:brightness-110 drop-shadow-sm" priority={true} loading="eager" fetchPriority="high" width={80} height={32} sizes="(max-width: 768px) 24px, 80px" />
                  </div>
                </div>
              </div>
            </Link>

            {/* Mobile Premium Brand Display */}
            <div className="flex flex-col items-center">
              <div className="text-sm font-black text-gray-900 tracking-tight">
                CARGUROO
              </div>
              <div className="text-[9px] text-primary font-bold tracking-wide uppercase">
                Mobile Mechanic
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              <Link to="/" className={`font-semibold text-sm transition-all duration-300 hover:text-primary relative ${location.pathname === '/' && activeSection === 'home' ? 'text-primary after:absolute after:bottom-[-8px] after:left-0 after:w-full after:h-0.5 after:bg-primary' : 'text-gray-700 hover:text-primary'}`} onClick={() => scrollToSection('home')}>
                HOME
              </Link>


              {/* Services Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center space-x-1 font-semibold text-sm text-gray-700 hover:text-primary transition-colors duration-300">
                  <span>SERVICES</span>
                  <ChevronDown className="w-4 h-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56 bg-white/95 backdrop-blur-lg border border-gray-200 shadow-xl">
                  {services.map(service => <DropdownMenuItem key={service.name} asChild>
                      <Link to={service.href} className="cursor-pointer hover:bg-primary/5 focus:bg-primary/5 w-full">
                        <service.icon className="w-4 h-4 mr-2" />
                        <span>{service.name}</span>
                      </Link>
                    </DropdownMenuItem>)}
                </DropdownMenuContent>
              </DropdownMenu>

              <Link to="/service-areas" className={`font-semibold text-sm transition-all duration-300 hover:text-primary relative ${location.pathname === '/service-areas' ? 'text-primary after:absolute after:bottom-[-8px] after:left-0 after:w-full after:h-0.5 after:bg-primary' : 'text-gray-700 hover:text-primary'}`}>
                AREAS
              </Link>

              <Link to="/blog" className={`font-semibold text-sm transition-all duration-300 hover:text-primary relative ${location.pathname === '/blog' ? 'text-primary after:absolute after:bottom-[-8px] after:left-0 after:w-full after:h-0.5 after:bg-primary' : 'text-gray-700 hover:text-primary'}`}>
                BLOG
              </Link>

              <Link to="/contact" className={`font-semibold text-sm transition-all duration-300 hover:text-primary relative ${location.pathname === '/contact' ? 'text-primary after:absolute after:bottom-[-8px] after:left-0 after:w-full after:h-0.5 after:bg-primary' : 'text-gray-700 hover:text-primary'}`}>
                CONTACT
              </Link>

              <Link to="/about" className={`font-semibold text-sm transition-all duration-300 hover:text-primary relative ${location.pathname === '/about' ? 'text-primary after:absolute after:bottom-[-8px] after:left-0 after:w-full after:h-0.5 after:bg-primary' : 'text-gray-700 hover:text-primary'}`}>
                ABOUT US
              </Link>
            </div>

            {/* Desktop CTA Buttons */}
            <div className="hidden lg:flex items-center space-x-3">
              <Link to="/get-quote">
                <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white transition-all duration-300 font-semibold">
                  <FileText className="w-4 h-4 mr-2" />
                  GET A QUOTE
                </Button>
              </Link>
              <Link to="/book-now">
                <Button className="bg-primary hover:bg-primary/90 text-white font-semibold px-6 py-2 shadow-lg hover:shadow-xl transition-all duration-300">
                  <Calendar className="w-4 h-4 mr-2" />
                  BOOK NOW
                </Button>
              </Link>
            </div>

            {/* Mobile menu button */}
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="lg:hidden p-2 rounded-lg text-gray-700 hover:text-primary hover:bg-gray-100 transition-colors duration-300" aria-label={isMobileMenuOpen ? "Close mobile menu" : "Open mobile menu"}>
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && <div className="lg:hidden absolute top-full left-0 right-0 bg-white/95 backdrop-blur-lg border-b border-gray-200 shadow-xl">
            <div className="px-4 py-6 space-y-4">
              
              {/* Mobile Contact Info */}
              <div className="border-b border-gray-200 pb-4 mb-4">
                <div className="flex items-center space-x-2 text-sm text-gray-600 mb-2">
                  <Phone className="w-4 h-4" />
                  <span>+61 440 134 765</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <MapPin className="w-4 h-4" />
                  <span>Service Areas</span>
                </div>
              </div>

              {/* Mobile Navigation Links */}
              <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="block px-4 py-3 text-gray-700 hover:bg-primary/5 hover:text-primary rounded-lg transition-colors duration-300 font-semibold">
                HOME
              </Link>

              
              <div className="px-4 py-2">
                <div className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Services</div>
                <div className="space-y-1">
                  {services.map(service => <Link key={service.name} to={service.href} onClick={() => setIsMobileMenuOpen(false)} className="flex items-center px-2 py-2 text-gray-700 hover:bg-primary/5 hover:text-primary rounded-lg transition-colors duration-300">
                      <service.icon className="w-4 h-4 mr-2" />
                      {service.name}
                    </Link>)}
                </div>
              </div>

              <Link to="/about" onClick={() => setIsMobileMenuOpen(false)} className="block px-4 py-3 text-gray-700 hover:bg-primary/5 hover:text-primary rounded-lg transition-colors duration-300 font-semibold">
                ABOUT US
              </Link>

              <Link to="/service-areas" onClick={() => setIsMobileMenuOpen(false)} className="block px-4 py-3 text-gray-700 hover:bg-primary/5 hover:text-primary rounded-lg transition-colors duration-300 font-semibold">
                AREAS
              </Link>

              <Link to="/blog" onClick={() => setIsMobileMenuOpen(false)} className="block px-4 py-3 text-gray-700 hover:bg-primary/5 hover:text-primary rounded-lg transition-colors duration-300 font-semibold">
                BLOG
              </Link>

              <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)} className="block px-4 py-3 text-gray-700 hover:bg-primary/5 hover:text-primary rounded-lg transition-colors duration-300 font-semibold">
                CONTACT
              </Link>

              {/* Mobile CTA Buttons */}
              <div className="pt-4 border-t border-gray-200 space-y-3">
                <Link to="/get-quote" onClick={() => setIsMobileMenuOpen(false)}>
                  <Button variant="outline" className="w-full border-primary text-primary hover:bg-primary hover:text-white transition-all duration-300 font-semibold">
                    <FileText className="w-4 h-4 mr-2" />
                    GET A QUOTE
                  </Button>
                </Link>
                <Link to="/book-now" onClick={() => setIsMobileMenuOpen(false)}>
                  <Button className="w-full bg-primary hover:bg-primary/90 text-white font-semibold shadow-lg">
                    <Calendar className="w-4 h-4 mr-2" />
                    BOOK NOW
                  </Button>
                </Link>
              </div>

              {/* Mobile Social Links */}
              <div className="flex items-center justify-center space-x-6 pt-4 border-t border-gray-200">
                <Facebook className="w-5 h-5 text-gray-400 hover:text-primary cursor-pointer transition-colors" />
                <Instagram className="w-5 h-5 text-gray-400 hover:text-primary cursor-pointer transition-colors" />
                <Youtube className="w-5 h-5 text-gray-400 hover:text-primary cursor-pointer transition-colors" />
              </div>
            </div>
          </div>}
      </nav>

      {/* Spacer for fixed navigation - zero gap */}
      {isVisible && <div className="hidden lg:block w-full h-[36px]" />}
    </>;
};